import moment from "moment";
import React, { useMemo } from "react";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import { avatarInitial } from "../utils/mailContactAvatar";

const bubbleMine =
  "max-w-[min(75vw,520px)] break-words [overflow-wrap:anywhere] rounded-lg rounded-tr-sm bg-wa-accentdeep px-2.5 py-1.5 text-[15px] leading-snug text-white shadow-sm";
const bubbleTheirs =
  "max-w-[min(75vw,520px)] break-words [overflow-wrap:anywhere] rounded-lg rounded-tl-sm border border-wa-border/70 bg-wa-panel2/85 px-2.5 py-1.5 text-[15px] leading-snug text-wa-text shadow-sm";

const ListChats = ({ chats, user }) => {
  const prepared = useMemo(() => {
    if (!chats?.length) return [];
    return [...chats]
      .sort((a, b) => new Date(b.datetime) - new Date(a.datetime))
      .map((chat) => ({
        ...chat,
        safeBody: sanitizeHtml(chat?.body),
      }));
  }, [chats]);

  const timeline = useMemo(() => {
    if (!prepared.length) return [];
    const rows = [];
    let lastDayKey = null;
    prepared.forEach((chat, i) => {
      const dayKey = moment(chat?.datetime).format("YYYY-MM-DD");
      if (dayKey !== lastDayKey) {
        lastDayKey = dayKey;
        rows.push({ kind: "day", dayKey, anchor: chat });
      }
      const prev = i > 0 ? prepared[i - 1] : null;
      rows.push({ kind: "msg", chat, prev });
    });
    return rows;
  }, [prepared]);

  if (!timeline.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-20 text-center text-sm text-wa-muted">
        Nog geen berichten in dit gesprek.
      </div>
    );
  }

  const uid = user?.user_id;

  return (
    <ul role="list" className="flex flex-col gap-1 px-2 py-4 sm:px-4">
      {timeline.map((row) => {
        if (row.kind === "day") {
          const chat = row.anchor;
          return (
            <li key={`day-${row.dayKey}`} className="my-2 flex justify-center">
              <span className="rounded-lg border border-wa-border/70 bg-wa-panel/90 px-3 py-1 text-xs font-medium text-wa-muted shadow-sm">
                {moment(chat?.datetime).calendar(null, {
                  sameDay: "[Vandaag]",
                  lastDay: "[Gisteren]",
                  lastWeek: "LL",
                  sameElse: "LL",
                })}
              </span>
            </li>
          );
        }

        const { chat, prev } = row;
        const isMine = Number(uid) === Number(chat?.author_id);
        const sameAuthorAsPrev =
          prev && Number(prev.author_id) === Number(chat.author_id);
        const showAvatar = !isMine && !sameAuthorAsPrev;

        return (
          <li
            key={chat.id}
            className={`flex w-full ${isMine ? "justify-end" : "justify-start"} ${
              sameAuthorAsPrev ? "mt-0.5" : "mt-2"
            }`}
          >
            <div
              className={`flex max-w-[min(92%,640px)] items-end gap-2 ${
                isMine ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <div className="w-8 flex-shrink-0">
                {!isMine &&
                  (showAvatar ? (
                    chat?.avatar ? (
                      <img
                        src={chat.avatar}
                        alt=""
                        className="h-8 w-8 rounded-full border border-wa-border/60 object-cover"
                      />
                    ) : (
                      <div
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-wa-border/70 bg-gradient-to-br from-wa-panel2 to-wa-panel text-xs font-semibold text-wa-text"
                        aria-hidden
                      >
                        {avatarInitial(chat?.author)}
                      </div>
                    )
                  ) : null)}
              </div>
              <div
                className={`min-w-0 ${isMine ? "items-end text-right" : "items-start text-left"} flex flex-col`}
              >
                {!isMine && showAvatar ? (
                  <span className="mb-1 px-1 text-xs font-medium text-wa-accent">{chat?.author}</span>
                ) : null}
                <div dangerouslySetInnerHTML={{ __html: chat.safeBody }} className={isMine ? bubbleMine : bubbleTheirs} />
                <div
                  className={`mt-1 flex items-center gap-1 px-1 text-[11px] text-wa-muted ${
                    isMine ? "justify-end" : "justify-start"
                  }`}
                >
                  <span>{moment(chat?.datetime).format("HH:mm")}</span>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default React.memo(ListChats);
